import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Chip,
  Snackbar,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation } from 'react-router-dom';
import { usePatientDiagnosis } from '../hooks/usePatientDiagnosis';


// Validation schema (basic for now, can be extended later)
const diagnosisSchema = yup.object({
  dateOfExam: yup.date().nullable(),
  
  height: yup.string(),
  weight: yup.string(),
  bloodPressure: yup.string(),
  tbTestGivenDate: yup.date().nullable(),
  tbTestReadDate: yup.date().nullable(),
  tbTestType: yup.string(),
  tbTestResult: yup.string(),
  tbResultsMm: yup.string(),
  tbActionTaken: yup.string(),
  chestXrayResults: yup.string(),
  tbStatus: yup.string(),
  primaryDiagnosis: yup.string().required('Primary diagnosis is required'),
  primaryCanManage: yup.string().required('Management capability is required'),
  primarySupervisionNeeded: yup.string(),
  secondaryDiagnosis: yup.string(),
  secondaryCanManage: yup.string(),
  secondarySupervisionNeeded: yup.string(),
  mildCognitiveImpairment: yup.boolean(),
  dementia: yup.boolean(),
  contagiousTreatment: yup.string(),
  contagiousCanManage: yup.string(),
  contagiousSupervisionNeeded: yup.string(),

  // Allergies and other conditions
  allergiesTreatment: yup.string(),
  allergiesCanManage: yup.string(),
  allergiesSupervisionNeeded: yup.string(),
  otherConditionsTreatment: yup.string(),
  otherConditionsCanManage: yup.string(),
  otherConditionsSupervisionNeeded: yup.string(),

  // Physical Health Status --- individual flags + assistive device + explanation fields
  auditoryImpairment: yup.boolean(),
  auditoryAssistiveDevice: yup.string(),
  auditoryExplain: yup.string(),

  visualImpairment: yup.boolean(),
  visualAssistiveDevice: yup.string(),
  visualExplain: yup.string(),

  wearsDentures: yup.boolean(),
  wearsDenturesAssistiveDevice: yup.string(),
  wearsDenturesExplain: yup.string(),

  wearsProsthesis: yup.boolean(),
  wearsProsthesisAssistiveDevice: yup.string(),
  wearsProsthesisExplain: yup.string(),

  specialDiet: yup.boolean(),
  specialDietExplain: yup.string(),

  substanceAbuseProblem: yup.boolean(),
  useOfAlcohol: yup.boolean(),
  useOfCigarettes: yup.boolean(),

  bowelImpairment: yup.boolean(),
  bladderImpairment: yup.boolean(),
  motorImpairment: yup.boolean(),
  requiresContinuousBedCare: yup.boolean(),
  historyOfSkinCondition: yup.boolean(),

  // Mental Condition fields (Yes/No plus explain)
  mentalConfused: yup.string(),
  mentalConfusedExplain: yup.string(),
  mentalInappropriate: yup.string(),
  mentalInappropriateExplain: yup.string(),
  mentalAggressive: yup.string(),
  mentalAggressiveExplain: yup.string(),
  mentalWandering: yup.string(),
  mentalWanderingExplain: yup.string(),
  mentalSundowning: yup.string(),
  mentalSundowningExplain: yup.string(),
  mentalAbleFollowInstructions: yup.string(),
  mentalAbleFollowInstructionsExplain: yup.string(),
  mentalDepressed: yup.string(),
  mentalDepressedExplain: yup.string(),
  mentalSuicidal: yup.string(),
  mentalSuicidalExplain: yup.string(),
  mentalAbleCommunicateNeeds: yup.string(),
  mentalAbleCommunicateNeedsExplain: yup.string(),
  mentalAtRiskGrooming: yup.string(),
  mentalAtRiskGroomingExplain: yup.string(),
  mentalAbleLeaveUnassisted: yup.string(),
  mentalAbleLeaveUnassistedExplain: yup.string(),

  // Capacity for self-care
  capBath: yup.string(),
  capBathExplain: yup.string(),
  capDressGroom: yup.string(),
  capDressGroomExplain: yup.string(),
  capFeed: yup.string(),
  capFeedExplain: yup.string(),
  capToileting: yup.string(),
  capToiletingExplain: yup.string(),
  capManageCash: yup.string(),
  capManageCashExplain: yup.string(),

  // Medication management
  medAdminPrescription: yup.string(),
  medAdminPrescriptionExplain: yup.string(),
  medAdminInjections: yup.string(),
  medAdminInjectionsExplain: yup.string(),
  medGlucoseTesting: yup.string(),
  medGlucoseTestingExplain: yup.string(),
  medAdminPRN: yup.string(),
  medAdminPRNExplain: yup.string(),
  medAdminOxygen: yup.string(),
  medAdminOxygenExplain: yup.string(),
  medStoreMedications: yup.string(),
  medStoreMedicationsExplain: yup.string(),

  // Ambulatory / Bedridden fields (new)
  ambulatoryStatus: yup.string(),
  ambulatoryStatusBasedOn: yup.string(),
  bedriddenIllness: yup.string(),
  bedriddenRecoveryFromSurgery: yup.string(),
  bedriddenOther: yup.string(),
  bedriddenDurationDays: yup.string(),
  bedriddenEstimatedDate: yup.date().nullable(),
  bedriddenPermanentExplain: yup.string(),

  // Hospice / Physical Health / Physician fields (new)
  receivingHospiceCare: yup.string(),
  hospiceTerminalIllness: yup.string(),
  physicalHealthStatus: yup.string(),
  comments: yup.string(),
  physicianNameAddress: yup.string(),
  physicianTelephone: yup.string(),
  physicianLengthOfTimePatient: yup.string(),
  physicianSignature: yup.string(),
  physicianDate: yup.date().nullable(),
});

type FormData = yup.InferType<typeof diagnosisSchema>;

// Mock residents for patient selection
const mockResidents = [
  {
    id: 1, name: 'John Smith', dob: '1954-03-15', sex: 'Male', allergies: ['Penicillin'], attendingPhysician: 'Dr. Johnson', room: '201', bp: '120/80', weight: '58Kg', height: '5ft 6in', bmi: '22', EMRNo: 'A12345'
  },
  { id: 2, name: 'Mary Johnson', dob: '1948-07-22', sex: 'Female', allergies: ['Latex', 'Peanuts'], attendingPhysician: 'Dr. Williams', room: '202', bp: '130/85', weight: '62Kg', height: '5ft 4in', bmi: '24', EMRNo: 'A12346' },
  { id: 3, name: 'Robert Williams', dob: '1965-11-08', sex: 'Male', allergies: [], attendingPhysician: 'Dr. Brown', room: '203', bp: '125/82', weight: '75Kg', height: '6ft 0in', bmi: '23', EMRNo: 'A12347' },
  { id: 4, name: 'Jennifer Brown', dob: '1972-05-30', sex: 'Female', allergies: ['Sulfa'], attendingPhysician: 'Dr. Davis', room: '204', bp: '118/78', weight: '55Kg', height: '5ft 5in', bmi: '21', EMRNo: 'A12348' },
];

const PatientDiagnosisPage = (): JSX.Element => {
  const location = useLocation();
  const [success, setSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedResident, setSelectedResident] = useState<string | null>(null); // Changed to string for API
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Initialize the patient diagnosis hook
  const {
    diagnosis,
    loading,
    saving,
    error,
    updateExamVitals,
    updateAmbulatoryStatus,
    updateDiagnosesTreatments,
    updatePhysicalHealthAssessment,
    updateMentalConditionAssessment,
    updateCapacityMedicationAssessment,
    updateHospicePhysicianInfo,
    submitDiagnosis,
    isEditable,
    completionPercentage,
  } = usePatientDiagnosis({ residentId: selectedResident });

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
    reset,
    getValues,
  } = useForm<FormData>({
    resolver: yupResolver(diagnosisSchema),
    defaultValues: {
      sex: '',
      tbTestResult: 'Negative',
      tbStatus: 'No Evidence of TB Infection or Disease',
      primaryCanManage: 'No',
      secondaryCanManage: 'No',
      contagiousCanManage: 'No',
      mildCognitiveImpairment: false,
      dementia: false,
      // defaults for new fields
      allergiesCanManage: 'No',
      otherConditionsCanManage: 'No',
      auditoryImpairment: false,
      visualImpairment: false,
      wearsDentures: false,
      wearsProsthesis: false,
      specialDiet: false,
      substanceAbuseProblem: false,
      useOfAlcohol: false,
      useOfCigarettes: false,
      bowelImpairment: false,
      bladderImpairment: false,
      motorImpairment: false,
      requiresContinuousBedCare: false,
      historyOfSkinCondition: false,
      // mental condition defaults
      mentalConfused: 'No',
      mentalInappropriate: 'No',
      mentalAggressive: 'No',
      mentalWandering: 'No',
      mentalSundowning: 'No',
      mentalAbleFollowInstructions: 'No',
      mentalDepressed: 'No',
      mentalSuicidal: 'No',
      mentalAbleCommunicateNeeds: 'No',
      mentalAtRiskGrooming: 'No',
      mentalAbleLeaveUnassisted: 'No',
      // capacity defaults
      capBath: 'No',
      capDressGroom: 'No',
      capFeed: 'No',
      capToileting: 'No',
      capManageCash: 'No',
      // medication management defaults
      medAdminPrescription: 'No',
      medAdminInjections: 'No',
      medGlucoseTesting: 'No',
      medAdminPRN: 'No',
      medAdminOxygen: 'No',
      medStoreMedications: 'No',

      // ambulatory defaults (new)
      ambulatoryStatus: 'Ambulatory',
      ambulatoryStatusBasedOn: '',
      bedriddenIllness: '',
      bedriddenRecoveryFromSurgery: '',
      bedriddenOther: '',
      bedriddenDurationDays: '',
      bedriddenEstimatedDate: null,
      bedriddenPermanentExplain: '',

      // hospice / physical health / physician defaults (new)
      receivingHospiceCare: 'No',
      hospiceTerminalIllness: '',
      physicalHealthStatus: 'Good',
      comments: '',
      physicianNameAddress: '',
      physicianTelephone: '',
      physicianLengthOfTimePatient: '',
      physicianSignature: '',
      physicianDate: null,
    } as any,
  });

  const steps = [
    'Exam & Vitals',
    'Ambulatory',
    'Diagnoses & Treatments',
    'Physical Health',
    'Mental Condition',
    'Capacity & Medication',
    'Hospice & Physician',
  ];

  // Populate form when diagnosis loads from API
  useEffect(() => {
    if (diagnosis) {
      const formData: any = {};

      // Exam & Vitals (Tab 1)
      if (diagnosis.examVitals) {
        formData.dateOfExam = diagnosis.examVitals.dateOfExam ? new Date(diagnosis.examVitals.dateOfExam) : null;
        formData.height = diagnosis.examVitals.height || '';
        formData.weight = diagnosis.examVitals.weight || '';
        formData.bloodPressure = diagnosis.examVitals.bloodPressure || '';
        formData.tbTestGivenDate = diagnosis.examVitals.tbTestGivenDate ? new Date(diagnosis.examVitals.tbTestGivenDate) : null;
        formData.tbTestReadDate = diagnosis.examVitals.tbTestReadDate ? new Date(diagnosis.examVitals.tbTestReadDate) : null;
        formData.tbTestType = diagnosis.examVitals.tbTestType || '';
        formData.tbTestResult = diagnosis.examVitals.tbTestResult || 'Negative';
        formData.tbResultsMm = diagnosis.examVitals.tbResultsMm || '';
        formData.tbActionTaken = diagnosis.examVitals.tbActionTaken || '';
        formData.chestXrayResults = diagnosis.examVitals.chestXrayResults || '';
        formData.tbStatus = diagnosis.examVitals.tbStatus || 'No Evidence of TB Infection or Disease';
      }

      // Ambulatory Status (Tab 2)
      if (diagnosis.ambulatoryStatus) {
        formData.ambulatoryStatus = diagnosis.ambulatoryStatus.ambulatoryStatus || 'Ambulatory';
        formData.ambulatoryStatusBasedOn = diagnosis.ambulatoryStatus.statusBasedOn || '';
        formData.bedriddenIllness = diagnosis.ambulatoryStatus.bedriddenIllness || '';
        formData.bedriddenRecoveryFromSurgery = diagnosis.ambulatoryStatus.bedriddenRecoveryFromSurgery || '';
        formData.bedriddenOther = diagnosis.ambulatoryStatus.bedriddenOther || '';
        formData.bedriddenDurationDays = diagnosis.ambulatoryStatus.bedriddenDurationDays?.toString() || '';
        formData.bedriddenEstimatedDate = diagnosis.ambulatoryStatus.bedriddenEstimatedEndDate ? new Date(diagnosis.ambulatoryStatus.bedriddenEstimatedEndDate) : null;
        formData.bedriddenPermanentExplain = diagnosis.ambulatoryStatus.bedriddenPermanentExplain || '';
      }

      // Diagnoses & Treatments (Tab 3)
      if (diagnosis.diagnosesTreatments) {
        formData.primaryDiagnosis = diagnosis.diagnosesTreatments.primaryDiagnosis || '';
        formData.primaryCanManage = diagnosis.diagnosesTreatments.primaryCanManage || 'No';
        formData.primarySupervisionNeeded = diagnosis.diagnosesTreatments.primarySupervisionNeeded || '';
        formData.secondaryDiagnosis = diagnosis.diagnosesTreatments.secondaryDiagnosis || '';
        formData.secondaryCanManage = diagnosis.diagnosesTreatments.secondaryCanManage || 'No';
        formData.secondarySupervisionNeeded = diagnosis.diagnosesTreatments.secondarySupervisionNeeded || '';
        formData.mildCognitiveImpairment = diagnosis.diagnosesTreatments.mildCognitiveImpairment || false;
        formData.dementia = diagnosis.diagnosesTreatments.dementia || false;
        formData.contagiousTreatment = diagnosis.diagnosesTreatments.contagiousTreatment || '';
        formData.contagiousCanManage = diagnosis.diagnosesTreatments.contagiousCanManage || 'No';
        formData.contagiousSupervisionNeeded = diagnosis.diagnosesTreatments.contagiousSupervisionNeeded || '';
        formData.allergiesTreatment = diagnosis.diagnosesTreatments.allergiesTreatment || '';
        formData.allergiesCanManage = diagnosis.diagnosesTreatments.allergiesCanManage || 'No';
        formData.allergiesSupervisionNeeded = diagnosis.diagnosesTreatments.allergiesSupervisionNeeded || '';
        formData.otherConditionsTreatment = diagnosis.diagnosesTreatments.otherConditionsTreatment || '';
        formData.otherConditionsCanManage = diagnosis.diagnosesTreatments.otherConditionsCanManage || 'No';
        formData.otherConditionsSupervisionNeeded = diagnosis.diagnosesTreatments.otherConditionsSupervisionNeeded || '';
      }

      // Physical Health (Tab 4) - Extract from assessmentData
      if (diagnosis.physicalHealthAssessment?.assessmentData) {
        const data = diagnosis.physicalHealthAssessment.assessmentData;
        formData.auditoryImpairment = data.auditoryImpairment || false;
        formData.auditoryAssistiveDevice = data.auditoryAssistiveDevice || '';
        formData.auditoryExplain = data.auditoryExplain || '';
        formData.visualImpairment = data.visualImpairment || false;
        formData.visualAssistiveDevice = data.visualAssistiveDevice || '';
        formData.visualExplain = data.visualExplain || '';
        formData.wearsDentures = data.wearsDentures || false;
        formData.wearsDenturesAssistiveDevice = data.wearsDenturesAssistiveDevice || '';
        formData.wearsDenturesExplain = data.wearsDenturesExplain || '';
        formData.wearsProsthesis = data.wearsProsthesis || false;
        formData.wearsProsthesisAssistiveDevice = data.wearsProsthesisAssistiveDevice || '';
        formData.wearsProsthesisExplain = data.wearsProsthesisExplain || '';
        formData.specialDiet = data.specialDiet || false;
        formData.specialDietExplain = data.specialDietExplain || '';
        formData.substanceAbuseProblem = data.substanceAbuseProblem || false;
        formData.useOfAlcohol = data.useOfAlcohol || false;
        formData.useOfCigarettes = data.useOfCigarettes || false;
        formData.bowelImpairment = data.bowelImpairment || false;
        formData.bladderImpairment = data.bladderImpairment || false;
        formData.motorImpairment = data.motorImpairment || false;
        formData.requiresContinuousBedCare = data.requiresContinuousBedCare || false;
        formData.historyOfSkinCondition = data.historyOfSkinCondition || false;
      }

      // Mental Condition (Tab 5) - Extract from assessmentData
      if (diagnosis.mentalConditionAssessment?.assessmentData) {
        const data = diagnosis.mentalConditionAssessment.assessmentData;
        formData.mentalConfused = data.mentalConfused || 'No';
        formData.mentalConfusedExplain = data.mentalConfusedExplain || '';
        formData.mentalInappropriate = data.mentalInappropriate || 'No';
        formData.mentalInappropriateExplain = data.mentalInappropriateExplain || '';
        formData.mentalAggressive = data.mentalAggressive || 'No';
        formData.mentalAggressiveExplain = data.mentalAggressiveExplain || '';
        formData.mentalWandering = data.mentalWandering || 'No';
        formData.mentalWanderingExplain = data.mentalWanderingExplain || '';
        formData.mentalSundowning = data.mentalSundowning || 'No';
        formData.mentalSundowningExplain = data.mentalSundowningExplain || '';
        formData.mentalAbleFollowInstructions = data.mentalAbleFollowInstructions || 'No';
        formData.mentalAbleFollowInstructionsExplain = data.mentalAbleFollowInstructionsExplain || '';
        formData.mentalDepressed = data.mentalDepressed || 'No';
        formData.mentalDepressedExplain = data.mentalDepressedExplain || '';
        formData.mentalSuicidal = data.mentalSuicidal || 'No';
        formData.mentalSuicidalExplain = data.mentalSuicidalExplain || '';
        formData.mentalAbleCommunicateNeeds = data.mentalAbleCommunicateNeeds || 'No';
        formData.mentalAbleCommunicateNeedsExplain = data.mentalAbleCommunicateNeedsExplain || '';
        formData.mentalAtRiskGrooming = data.mentalAtRiskGrooming || 'No';
        formData.mentalAtRiskGroomingExplain = data.mentalAtRiskGroomingExplain || '';
        formData.mentalAbleLeaveUnassisted = data.mentalAbleLeaveUnassisted || 'No';
        formData.mentalAbleLeaveUnassistedExplain = data.mentalAbleLeaveUnassistedExplain || '';
      }

      // Capacity & Medication (Tab 6) - Extract from assessmentData
      if (diagnosis.capacityMedicationAssessment?.assessmentData) {
        const data = diagnosis.capacityMedicationAssessment.assessmentData;
        formData.capBath = data.capBath || 'No';
        formData.capBathExplain = data.capBathExplain || '';
        formData.capDressGroom = data.capDressGroom || 'No';
        formData.capDressGroomExplain = data.capDressGroomExplain || '';
        formData.capFeed = data.capFeed || 'No';
        formData.capFeedExplain = data.capFeedExplain || '';
        formData.capToileting = data.capToileting || 'No';
        formData.capToiletingExplain = data.capToiletingExplain || '';
        formData.capManageCash = data.capManageCash || 'No';
        formData.capManageCashExplain = data.capManageCashExplain || '';
        formData.medAdminPrescription = data.medAdminPrescription || 'No';
        formData.medAdminPrescriptionExplain = data.medAdminPrescriptionExplain || '';
        formData.medAdminInjections = data.medAdminInjections || 'No';
        formData.medAdminInjectionsExplain = data.medAdminInjectionsExplain || '';
        formData.medGlucoseTesting = data.medGlucoseTesting || 'No';
        formData.medGlucoseTestingExplain = data.medGlucoseTestingExplain || '';
        formData.medAdminPRN = data.medAdminPRN || 'No';
        formData.medAdminPRNExplain = data.medAdminPRNExplain || '';
        formData.medAdminOxygen = data.medAdminOxygen || 'No';
        formData.medAdminOxygenExplain = data.medAdminOxygenExplain || '';
        formData.medStoreMedications = data.medStoreMedications || 'No';
        formData.medStoreMedicationsExplain = data.medStoreMedicationsExplain || '';
      }

      // Hospice & Physician (Tab 7)
      if (diagnosis.hospicePhysicianInfo) {
        formData.receivingHospiceCare = diagnosis.hospicePhysicianInfo.receivingHospiceCare || 'No';
        formData.hospiceTerminalIllness = diagnosis.hospicePhysicianInfo.hospiceTerminalIllness || '';
        formData.physicalHealthStatus = diagnosis.hospicePhysicianInfo.physicalHealthStatus || 'Good';
        formData.comments = diagnosis.hospicePhysicianInfo.comments || '';
        formData.physicianNameAddress = diagnosis.hospicePhysicianInfo.physicianNameAddress || '';
        formData.physicianTelephone = diagnosis.hospicePhysicianInfo.physicianTelephone || '';
        formData.physicianLengthOfTimePatient = diagnosis.hospicePhysicianInfo.physicianLengthOfTimePatient || '';
        formData.physicianSignature = diagnosis.hospicePhysicianInfo.physicianSignature || '';
        formData.physicianDate = diagnosis.hospicePhysicianInfo.physicianDate ? new Date(diagnosis.hospicePhysicianInfo.physicianDate) : null;
      }

      reset(formData);
    }
  }, [diagnosis, reset]);

  // Auto-save current tab before moving forward
  const saveCurrentTab = async (tabIndex: number) => {
    const formData = getValues();

    switch (tabIndex) {
      case 0: // Exam & Vitals
        await updateExamVitals({
          dateOfExam: formData.dateOfExam ? new Date(formData.dateOfExam).toISOString().split('T')[0] : undefined,
          height: formData.height,
          weight: formData.weight,
          bloodPressure: formData.bloodPressure,
          tbTestGivenDate: formData.tbTestGivenDate ? new Date(formData.tbTestGivenDate).toISOString().split('T')[0] : undefined,
          tbTestReadDate: formData.tbTestReadDate ? new Date(formData.tbTestReadDate).toISOString().split('T')[0] : undefined,
          tbTestType: formData.tbTestType,
          tbTestResult: formData.tbTestResult,
          tbResultsMm: formData.tbResultsMm,
          tbActionTaken: formData.tbActionTaken,
          chestXrayResults: formData.chestXrayResults,
          tbStatus: formData.tbStatus,
        });
        break;

      case 1: // Ambulatory Status
        await updateAmbulatoryStatus({
          ambulatoryStatus: formData.ambulatoryStatus,
          statusBasedOn: formData.ambulatoryStatusBasedOn,
          bedriddenIllness: formData.bedriddenIllness,
          bedriddenRecoveryFromSurgery: formData.bedriddenRecoveryFromSurgery,
          bedriddenOther: formData.bedriddenOther,
          bedriddenDurationDays: formData.bedriddenDurationDays ? parseInt(formData.bedriddenDurationDays, 10) : undefined,
          bedriddenEstimatedEndDate: formData.bedriddenEstimatedDate ? new Date(formData.bedriddenEstimatedDate).toISOString().split('T')[0] : undefined,
          bedriddenPermanentExplain: formData.bedriddenPermanentExplain,
        });
        break;

      case 2: // Diagnoses & Treatments
        await updateDiagnosesTreatments({
          primaryDiagnosis: formData.primaryDiagnosis,
          primaryCanManage: formData.primaryCanManage,
          primarySupervisionNeeded: formData.primarySupervisionNeeded,
          secondaryDiagnosis: formData.secondaryDiagnosis,
          secondaryCanManage: formData.secondaryCanManage,
          secondarySupervisionNeeded: formData.secondarySupervisionNeeded,
          mildCognitiveImpairment: formData.mildCognitiveImpairment,
          dementia: formData.dementia,
          contagiousTreatment: formData.contagiousTreatment,
          contagiousCanManage: formData.contagiousCanManage,
          contagiousSupervisionNeeded: formData.contagiousSupervisionNeeded,
          allergiesTreatment: formData.allergiesTreatment,
          allergiesCanManage: formData.allergiesCanManage,
          allergiesSupervisionNeeded: formData.allergiesSupervisionNeeded,
          otherConditionsTreatment: formData.otherConditionsTreatment,
          otherConditionsCanManage: formData.otherConditionsCanManage,
          otherConditionsSupervisionNeeded: formData.otherConditionsSupervisionNeeded,
        });
        break;

      case 3: // Physical Health Assessment
        await updatePhysicalHealthAssessment({
          assessmentData: {
            auditoryImpairment: formData.auditoryImpairment,
            auditoryAssistiveDevice: formData.auditoryAssistiveDevice,
            auditoryExplain: formData.auditoryExplain,
            visualImpairment: formData.visualImpairment,
            visualAssistiveDevice: formData.visualAssistiveDevice,
            visualExplain: formData.visualExplain,
            wearsDentures: formData.wearsDentures,
            wearsDenturesAssistiveDevice: formData.wearsDenturesAssistiveDevice,
            wearsDenturesExplain: formData.wearsDenturesExplain,
            wearsProsthesis: formData.wearsProsthesis,
            wearsProsthesisAssistiveDevice: formData.wearsProsthesisAssistiveDevice,
            wearsProsthesisExplain: formData.wearsProsthesisExplain,
            specialDiet: formData.specialDiet,
            specialDietExplain: formData.specialDietExplain,
            substanceAbuseProblem: formData.substanceAbuseProblem,
            useOfAlcohol: formData.useOfAlcohol,
            useOfCigarettes: formData.useOfCigarettes,
            bowelImpairment: formData.bowelImpairment,
            bladderImpairment: formData.bladderImpairment,
            motorImpairment: formData.motorImpairment,
            requiresContinuousBedCare: formData.requiresContinuousBedCare,
            historyOfSkinCondition: formData.historyOfSkinCondition,
          },
        });
        break;

      case 4: // Mental Condition Assessment
        await updateMentalConditionAssessment({
          assessmentData: {
            mentalConfused: formData.mentalConfused,
            mentalConfusedExplain: formData.mentalConfusedExplain,
            mentalInappropriate: formData.mentalInappropriate,
            mentalInappropriateExplain: formData.mentalInappropriateExplain,
            mentalAggressive: formData.mentalAggressive,
            mentalAggressiveExplain: formData.mentalAggressiveExplain,
            mentalWandering: formData.mentalWandering,
            mentalWanderingExplain: formData.mentalWanderingExplain,
            mentalSundowning: formData.mentalSundowning,
            mentalSundowningExplain: formData.mentalSundowningExplain,
            mentalAbleFollowInstructions: formData.mentalAbleFollowInstructions,
            mentalAbleFollowInstructionsExplain: formData.mentalAbleFollowInstructionsExplain,
            mentalDepressed: formData.mentalDepressed,
            mentalDepressedExplain: formData.mentalDepressedExplain,
            mentalSuicidal: formData.mentalSuicidal,
            mentalSuicidalExplain: formData.mentalSuicidalExplain,
            mentalAbleCommunicateNeeds: formData.mentalAbleCommunicateNeeds,
            mentalAbleCommunicateNeedsExplain: formData.mentalAbleCommunicateNeedsExplain,
            mentalAtRiskGrooming: formData.mentalAtRiskGrooming,
            mentalAtRiskGroomingExplain: formData.mentalAtRiskGroomingExplain,
            mentalAbleLeaveUnassisted: formData.mentalAbleLeaveUnassisted,
            mentalAbleLeaveUnassistedExplain: formData.mentalAbleLeaveUnassistedExplain,
          },
        });
        break;

      case 5: // Capacity & Medication Assessment
        await updateCapacityMedicationAssessment({
          assessmentData: {
            capBath: formData.capBath,
            capBathExplain: formData.capBathExplain,
            capDressGroom: formData.capDressGroom,
            capDressGroomExplain: formData.capDressGroomExplain,
            capFeed: formData.capFeed,
            capFeedExplain: formData.capFeedExplain,
            capToileting: formData.capToileting,
            capToiletingExplain: formData.capToiletingExplain,
            capManageCash: formData.capManageCash,
            capManageCashExplain: formData.capManageCashExplain,
            medAdminPrescription: formData.medAdminPrescription,
            medAdminPrescriptionExplain: formData.medAdminPrescriptionExplain,
            medAdminInjections: formData.medAdminInjections,
            medAdminInjectionsExplain: formData.medAdminInjectionsExplain,
            medGlucoseTesting: formData.medGlucoseTesting,
            medGlucoseTestingExplain: formData.medGlucoseTestingExplain,
            medAdminPRN: formData.medAdminPRN,
            medAdminPRNExplain: formData.medAdminPRNExplain,
            medAdminOxygen: formData.medAdminOxygen,
            medAdminOxygenExplain: formData.medAdminOxygenExplain,
            medStoreMedications: formData.medStoreMedications,
            medStoreMedicationsExplain: formData.medStoreMedicationsExplain,
          },
        });
        break;

      case 6: // Hospice & Physician Info
        await updateHospicePhysicianInfo({
          receivingHospiceCare: formData.receivingHospiceCare,
          hospiceTerminalIllness: formData.hospiceTerminalIllness,
          physicalHealthStatus: formData.physicalHealthStatus,
          comments: formData.comments,
          physicianNameAddress: formData.physicianNameAddress,
          physicianTelephone: formData.physicianTelephone,
          physicianLengthOfTimePatient: formData.physicianLengthOfTimePatient,
          physicianSignature: formData.physicianSignature,
          physicianDate: formData.physicianDate ? new Date(formData.physicianDate).toISOString().split('T')[0] : undefined,
        });
        break;
    }
  };

  const onFinalSubmit = async (data: FormData) => {
    try {
      // Save current tab first
      await saveCurrentTab(activeStep);

      // Then submit diagnosis
      await submitDiagnosis();

      setSuccess(true);
      setSnackbar({
        open: true,
        message: 'Diagnosis submitted successfully!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to submit diagnosis',
        severity: 'error',
      });
    }
  };

  const stepFieldMap: Record<number, (keyof FormData)[]> = {
    0: [
      'dateOfExam',
      
      'height',
      'weight',
      'bloodPressure',
      'tbTestGivenDate',
      'tbTestReadDate',
      'tbTestType',
      'tbTestResult',
      'tbResultsMm',
      'tbActionTaken',
      'chestXrayResults',
      'tbStatus',
    ],
    1: ['ambulatoryStatus', 'ambulatoryStatusBasedOn'],
    2: [
      // primaryDiagnosis and primaryCanManage are required on final submit but
      // should not block navigation between steps, so they are not included here.
      'secondaryDiagnosis',
      'secondaryCanManage',
      'contagiousTreatment',
      'contagiousCanManage',
      'allergiesTreatment',
      'allergiesCanManage',
      'otherConditionsTreatment',
      'otherConditionsCanManage',
    ],
    3: [
      'auditoryImpairment',
      'visualImpairment',
      'wearsDentures',
      'wearsProsthesis',
      'specialDiet',
      'substanceAbuseProblem',
      'bowelImpairment',
      'bladderImpairment',
      'motorImpairment',
      'requiresContinuousBedCare',
      'historyOfSkinCondition',
    ],
    4: [
      'mentalConfused',
      'mentalInappropriate',
      'mentalAggressive',
      'mentalWandering',
      'mentalSundowning',
      'mentalAbleFollowInstructions',
      'mentalDepressed',
      'mentalSuicidal',
      'mentalAbleCommunicateNeeds',
      'mentalAtRiskGrooming',
      'mentalAbleLeaveUnassisted',
    ],
    5: [
      'capBath',
      'capDressGroom',
      'capFeed',
      'capToileting',
      'capManageCash',
      'medAdminPrescription',
      'medAdminInjections',
      'medGlucoseTesting',
      'medAdminPRN',
      'medAdminOxygen',
      'medStoreMedications',
    ],
    6: [
      'receivingHospiceCare',
      'hospiceTerminalIllness',
      'physicalHealthStatus',
      'comments',
      'physicianNameAddress',
      'physicianTelephone',
      'physicianLengthOfTimePatient',
      'physicianSignature',
      'physicianDate',
    ],
  };

  const handleNext = async () => {
    const fields = stepFieldMap[activeStep];
    let valid = true;
    if (fields && fields.length) {
      valid = await trigger(fields as any);
    }

    if (valid) {
      try {
        // Auto-save current tab before moving to next
        await saveCurrentTab(activeStep);
        setSnackbar({
          open: true,
          message: 'Progress saved successfully',
          severity: 'success',
        });
        setActiveStep((s) => s + 1);
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error.message || 'Failed to save progress',
          severity: 'error',
        });
      }
    }
  };

  const handleBack = () => setActiveStep((s) => s - 1);
  const watchedValues = (useWatch({ control }) || {}) as Partial<FormData>;

  const isExplainEnabled = (key: keyof FormData): boolean => {
    const value = watchedValues?.[key];
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'yes';
    }
    return Boolean(value);
  };

  // Handle navigation from Residents page
  React.useEffect(() => {
    const state: any = location.state;
    if (state && state.residentId) {
      setSelectedResident(String(state.residentId));
    } else if (selectedResident === null) {
      // Default to first resident if no navigation state
      setSelectedResident('1');
    }
  }, [location.state]);


  const renderStepContent = (step: number) => {
    if (step === 0) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller
                name="dateOfExam"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date of Exam"
                    value={field.value}
                    onChange={field.onChange}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          {/* <Grid item xs={12} sm={4}>
            <Controller
              name="sex"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.sex}>
                  <InputLabel>Sex</InputLabel>
                  <Select {...field} label="Sex">
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {errors.sex && <FormHelperText>{errors.sex.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="height" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Height" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="weight" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Weight" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="bloodPressure" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Blood Pressure" />
            )} />
          </Grid> */}

          {/* TB Test Section */}
          <Grid item xs={12}>
            <Typography variant="h6">Tuberculosis (TB) Test</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller name="tbTestGivenDate" control={control} render={({ field }) => (
                <DatePicker label="Date TB Test Given" value={field.value} onChange={field.onChange} slotProps={{ textField: { fullWidth: true } }} />
              )} />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller name="tbTestReadDate" control={control} render={({ field }) => (
                <DatePicker label="Date TB Test Read" value={field.value} onChange={field.onChange} slotProps={{ textField: { fullWidth: true } }} />
              )} />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="tbTestType" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Type of TB Test" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="tbTestResult" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>TB Test Result</InputLabel>
                <Select {...field} label="TB Test Result">
                  <MenuItem value="Negative">Negative</MenuItem>
                  <MenuItem value="Positive">Positive</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="tbResultsMm" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Results (mm)" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="tbActionTaken" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Action Taken (if positive)" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Controller name="chestXrayResults" control={control} render={({ field }) => (
              <TextField {...field} fullWidth multiline rows={3} label="Chest X-ray Results" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Controller name="tbStatus" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>TB Status</InputLabel>
                <Select {...field} label="TB Status">
                  <MenuItem value="Active TB Disease">Active TB Disease</MenuItem>
                  <MenuItem value="Latent TB Infection">Latent TB Infection</MenuItem>
                  <MenuItem value="No Evidence of TB Infection or Disease">No Evidence of TB Infection or Disease</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>
        </Grid>
      );
    }

    // Step 1 - Ambulatory
    if (step === 1) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Ambulatory Status</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="ambulatoryStatus" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Ambulatory Status</InputLabel>
                <Select {...field} label="Ambulatory Status">
                  <MenuItem value="Ambulatory">Ambulatory</MenuItem>
                  <MenuItem value="Nonambulatory">Nonambulatory</MenuItem>
                  <MenuItem value="Bedridden">Bedridden</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller name="ambulatoryStatusBasedOn" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>If nonambulatory/bedridden, status based upon</InputLabel>
                <Select {...field} label="Status Based Upon">
                  <MenuItem value="Physical Condition">Physical Condition</MenuItem>
                  <MenuItem value="Mental Condition">Mental Condition</MenuItem>
                  <MenuItem value="Both Physical and Mental Condition">Both Physical and Mental Condition</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">If bedridden, check one or more of the following and describe the nature of the illness, surgery or otherwise</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenIllness" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Illness" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenRecoveryFromSurgery" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Recovery from Surgery" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenOther" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Other" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenDurationDays" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="If bedridden, how long (number of days)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller name="bedriddenEstimatedDate" control={control} render={({ field }) => (
                <DatePicker label="Estimated date illness or recovery is expected to end" value={field.value} onChange={field.onChange} slotProps={{ textField: { fullWidth: true } }} />
              )} />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="bedriddenPermanentExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="If illness or recovery is permanent, please explain" />
            )} />
          </Grid>
        </Grid>
      );
    }

    // Step 2 - Diagnoses & Treatments (primary, secondary, contagious, allergies, other)
    if (step === 2) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Primary Diagnosis</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller name="primaryDiagnosis" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Primary Diagnosis (treatment/medication/type)" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="primaryCanManage" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Can manage own treatment?</InputLabel>
                <Select {...field} label="Can manage own treatment?">
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller name="primarySupervisionNeeded" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="If not, type of medical supervision needed" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Secondary Diagnosis(es)</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller name="secondaryDiagnosis" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Secondary Diagnosis (treatment/medication)" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="secondaryCanManage" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Can manage own treatment?</InputLabel>
                <Select {...field} label="Can manage own treatment?">
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller name="secondarySupervisionNeeded" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="If not, type of medical supervision needed" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1">Check if applicable:</Typography>
            <Controller name="mildCognitiveImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Mild Cognitive Impairment" />
            )} />
            <Controller name="dementia" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Dementia" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Contagious / Infectious Disease</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller name="contagiousTreatment" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Treatment/medication (type and dosage/equipment)" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="contagiousCanManage" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Can manage own treatment?</InputLabel>
                <Select {...field} label="Can manage own treatment?">
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller name="contagiousSupervisionNeeded" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="If not, type of medical supervision needed" />
            )} />
          </Grid>

          {/* Allergies & Other Conditions */}
          <Grid item xs={12}>
            <Typography variant="h6">Allergies</Typography>
          </Grid>
          <Grid item xs={12}>
            <Controller name="allergiesTreatment" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Treatment/medication (type and dosage/equipment)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="allergiesCanManage" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Can manage own treatment?</InputLabel>
                <Select {...field} label="Can manage own treatment?">
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller name="allergiesSupervisionNeeded" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="If not, type of medical supervision needed" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Other Conditions</Typography>
          </Grid>
          <Grid item xs={12}>
            <Controller name="otherConditionsTreatment" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Treatment/medication (type and dosage/equipment)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="otherConditionsCanManage" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Can manage own treatment?</InputLabel>
                <Select {...field} label="Can manage own treatment?">
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller name="otherConditionsSupervisionNeeded" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="If not, type of medical supervision needed" />
            )} />
          </Grid>
        </Grid>
      );
    }

    // Step 3 - Physical Health
    if (step === 3) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Physical Health Status</Typography>
          </Grid>

          {/** Auditory */}
          <Grid item xs={12} sm={4}>
            <Controller name="auditoryImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Auditory Impairment" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="auditoryAssistiveDevice" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Assistive Device (if applicable)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="auditoryExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain" />
            )} />
          </Grid>

          {/** Visual */}
          <Grid item xs={12} sm={4}>
            <Controller name="visualImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Visual Impairment" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="visualAssistiveDevice" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Assistive Device (if applicable)" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="visualExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain" />
            )} />
          </Grid>

          {/** Dentures / Prosthesis */}
          <Grid item xs={12} sm={4}>
            <Controller name="wearsDentures" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Wears Dentures" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="wearsDenturesAssistiveDevice" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Assistive Device" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="wearsDenturesExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="wearsProsthesis" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Wears Prosthesis" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="wearsProsthesisAssistiveDevice" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Assistive Device" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="wearsProsthesisExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain" />
            )} />
          </Grid>

          {/** Other items */}
          <Grid item xs={12} sm={4}>
            <Controller name="specialDiet" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Special Diet" />
            )} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller name="specialDietExplain" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Explain (if applicable)" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="substanceAbuseProblem" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Substance Abuse Problem" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="useOfAlcohol" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Use of Alcohol" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="useOfCigarettes" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Use of Cigarettes" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="bowelImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Bowel Impairment" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="bladderImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Bladder Impairment" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="motorImpairment" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Motor Impairment/Paralysis" />
            )} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller name="requiresContinuousBedCare" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Requires Continuous Bed Care" />
            )} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="historyOfSkinCondition" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="History of Skin Condition or Breakdown" />
            )} />
          </Grid>
        </Grid>
      );
    }

    // Step 4 - Mental Condition
    if (step === 4) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Mental Condition</Typography>
          </Grid>

          {[
            { key: 'mentalConfused', label: 'Confused/Disoriented' },
            { key: 'mentalInappropriate', label: 'Inappropriate Behavior' },
            { key: 'mentalAggressive', label: 'Aggressive Behavior' },
            { key: 'mentalWandering', label: 'Wandering Behavior' },
            { key: 'mentalSundowning', label: 'Sundowning Behavior' },
            { key: 'mentalAbleFollowInstructions', label: 'Able to Follow Instructions' },
            { key: 'mentalDepressed', label: 'Depressed' },
            { key: 'mentalSuicidal', label: 'Suicidal/Self-Abuse' },
            { key: 'mentalAbleCommunicateNeeds', label: 'Able to Communicate Needs' },
            { key: 'mentalAtRiskGrooming', label: 'At Risk if Allowed Direct Access to Personal Grooming/Hygiene Items' },
            { key: 'mentalAbleLeaveUnassisted', label: 'Able to Leave Facility Unassisted' },
          ].map((item) => (
            <React.Fragment key={item.key}>

              {/* Checkbox */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <Controller
                    name={item.key as any}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value === 'yes' || field.value === true} // handle both yes/string or boolean
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label={item.label}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Explain field */}
              <Grid item xs={12} sm={8}>
                <Controller
                  name={`${item.key}Explain` as any}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Explain"
                      disabled={!isExplainEnabled(item.key as keyof FormData)} // <-- editable only if checkbox is checked
                    />
                  )}
                />
              </Grid>

            </React.Fragment>
          ))}
        </Grid>
      );
    }




    // Step 5 - Capacity & Medication
    if (step === 5) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Capacity for Self-Care</Typography>
          </Grid>
          {[
            { key: 'capBath', label: 'Able to Bathe Self' },
            { key: 'capDressGroom', label: 'Able to Dress/Groom Self' },
            { key: 'capFeed', label: 'Able to Feed Self' },
            { key: 'capToileting', label: 'Able to Care for Own Toileting Needs' },
            { key: 'capManageCash', label: 'Able to Manage Own Cash Resources' },
          ].map((item) => (
            <React.Fragment key={item.key}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>{item.label}</InputLabel>
                  <Controller name={item.key as any} control={control} render={({ field }) => (
                    <Select {...field} label={item.label}>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  )} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Controller name={`${item.key}Explain` as any} control={control} render={({ field }) => (
                  <TextField {...field} fullWidth label="Explain" />
                )} />
              </Grid>
            </React.Fragment>
          ))}

          <Grid item xs={12}>
            <Typography variant="h6">Medication Management</Typography>
          </Grid>
          {[
            { key: 'medAdminPrescription', label: 'Able to Administer Own Prescription Medications' },
            { key: 'medAdminInjections', label: 'Able to Administer Own Injections' },
            { key: 'medGlucoseTesting', label: 'Able to Perform Own Glucose Testing' },
            { key: 'medAdminPRN', label: 'Able to Administer Own PRN Medications' },
            { key: 'medAdminOxygen', label: 'Able to Administer Own Oxygen' },
            { key: 'medStoreMedications', label: 'Able to Store Own Medications' },
          ].map((item) => (
            <React.Fragment key={item.key}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>{item.label}</InputLabel>
                  <Controller name={item.key as any} control={control} render={({ field }) => (
                    <Select {...field} label={item.label}>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  )} />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Controller name={`${item.key}Explain` as any} control={control} render={({ field }) => (
                  <TextField {...field} fullWidth label="Explain" />
                )} />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      );
    }

    // Step 6 - Hospice / Physician
    if (step === 6) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Hospice / Physical Health / Physician Information</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="receivingHospiceCare" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Receiving Hospice Care?</InputLabel>
                <Select {...field} label="Receiving Hospice Care?">
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller name="hospiceTerminalIllness" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="If yes, specify the terminal illness" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="physicalHealthStatus" control={control} render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Physical Health Status</InputLabel>
                <Select {...field} label="Physical Health Status">
                  <MenuItem value="Good">Good</MenuItem>
                  <MenuItem value="Fair">Fair</MenuItem>
                  <MenuItem value="Poor">Poor</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>

          <Grid item xs={12}>
            <Controller name="comments" control={control} render={({ field }) => (
              <TextField {...field} fullWidth multiline rows={4} label="Comments" />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1">Physician Information</Typography>
          </Grid>

          <Grid item xs={12}>
            <Controller name="physicianNameAddress" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Physician Name and Address (print)" />
            )} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller name="physicianTelephone" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Telephone" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="physicianLengthOfTimePatient" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Length of time resident has been your patient" />
            )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Controller name="physicianDate" control={control} render={({ field }) => (
                <DatePicker label="Date" value={field.value} onChange={field.onChange} slotProps={{ textField: { fullWidth: true } }} />
              )} />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <Controller name="physicianSignature" control={control} render={({ field }) => (
              <TextField {...field} fullWidth label="Physician Signature" />
            )} />
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const getResident = (id: string | null) =>
    mockResidents.find((r) => String(r.id) === String(id)) || null;
  const calcAge = (dob: string) => {
    try {
      const birth = new Date(dob);
      const diff = Date.now() - birth.getTime();
      const ageDt = new Date(diff);
      return Math.abs(ageDt.getUTCFullYear() - 1970);
    } catch {
      return '';
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Patient Diagnosis
      </Typography>

      {/* Loading Indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Saving Indicator */}
      {saving && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={20} />}>
          Saving changes...
        </Alert>
      )}

      {/* Diagnosis Status & Progress */}
      {diagnosis && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Status:</strong> {diagnosis.status} | <strong>Completion:</strong> {completionPercentage}%
          {!isEditable && ' (Read-only - Diagnosis already completed)'}
        </Alert>
      )}

      {/* Patient Details Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Patient</InputLabel>
              <Select
                value={selectedResident || ''}
                onChange={(e) => setSelectedResident(e.target.value as string)}
                label="Select Patient"
              >
                {mockResidents.map((r) => (
                  <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={9}>
            {selectedResident ? (
              (() => {
                const r = getResident(selectedResident);
                if (!r) {
                  return (
                    <Typography variant="body2" color="textSecondary">
                      Patient details are unavailable for the selected resident.
                    </Typography>
                  );
                }

                return (
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Typography variant="h6">
                        Name: {r.name} &nbsp;&nbsp; &nbsp; EMR Number: {r.EMRNo}
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ fontWeight: 'bold' }}>BP:</span> {r.bp} &nbsp;&nbsp;
                        <span style={{ fontWeight: 'bold' }}>Height:</span> {r.height} &nbsp;&nbsp;
                        <span style={{ fontWeight: 'bold' }}>Weight:</span> {r.weight} &nbsp;&nbsp;
                        <span style={{ fontWeight: 'bold' }}>BMI:</span> {r.bmi}
                      </Typography>

                      <Typography variant="body2">
                        <span style={{ fontWeight: 'bold' }}>Room:</span> {r.room} &nbsp;&nbsp;
                        <span style={{ fontWeight: 'bold' }}>Age:</span> {calcAge(r.dob)} &nbsp;&nbsp;
                        <span style={{ fontWeight: 'bold' }}>Sex:</span> {r.sex}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        Attending Physician: <strong>{r.attendingPhysician}</strong>
                      </Typography>
                      <Chip label="Allergies" size="small" />
                      <Box sx={{ mt: 1 }}>
                        {r.allergies && r.allergies.length > 0 ? (
                          r.allergies.map((a: string) => (
                            <Chip key={a} label={a} color="error" size="small" sx={{ mr: 1 }} />
                          ))
                        ) : (
                          <Chip label="No Known Allergies" size="small" />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                );
              })()
            ) : (
              <Typography variant="body2">Select a patient to view details.</Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {success && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Alert severity="success">Patient diagnosis saved successfully.</Alert>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit(onFinalSubmit)}>
          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Box>
              {activeStep > 0 && (
                <Button onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
              )}
            </Box>

            <Box>
              <Button variant="outlined" onClick={() => { reset(); setSuccess(false); }} sx={{ mr: 2 }}>Reset</Button>
              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={handleNext}>Next</Button>
              ) : (
                <Button type="submit" variant="contained">Save Diagnosis</Button>
              )}
            </Box>
          </Box>
        </form>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientDiagnosisPage;
